import React from 'react';
import { Control, Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Section 5 of the 5-section campaign registration layout ("기타").
 *
 * Scope: secondary-use submission items table, the submission path
 * input, and the secondary-use guide textarea. These used to live at
 * the bottom of `CampaignMissionV2`; pulling them into their own card
 * matches the reference layout (see frontend/tmp/campaign_register_layout.html §5).
 *
 * Field names (data model) are unchanged:
 * `secondarySubmissions[]`, `submissionPath`, `secondarySubmissionGuide`.
 */
const SECONDARY_TYPES = ['video', 'photo'] as const;

interface Props {
  control: Control<any>;
  watch: any;
}

const CampaignOtherV2: React.FC<Props> = ({ control, watch }) => {
  const { t } = useTranslation(['campaigns', 'common']);

  const {
    fields: secondaryFields,
    append: secondaryAppend,
    remove: secondaryRemove,
  } = useFieldArray({ control, name: 'secondarySubmissions' });

  return (
    <div className="space-y-5">
      {/* 2차 활용 제출 항목 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('campaigns:registration.v2.secondarySubmissions')}
        </label>
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600">
                {t('campaigns:registration.v2.itemSelect')}
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">
                {t('campaigns:registration.v2.quantityVolume')}
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">
                {t('campaigns:registration.v2.remarks')}
              </th>
              <th className="px-3 py-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {secondaryFields.map((f, i) => {
              const itemType = watch(`secondarySubmissions.${i}.type`);
              return (
                <tr key={f.id} className="border-t border-gray-100">
                  <td className="px-3 py-2">
                    <Controller
                      name={`secondarySubmissions.${i}.type`}
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          className="px-2 py-1 text-sm border border-gray-300 rounded"
                        >
                          <option value="">{t('common:select')}</option>
                          {SECONDARY_TYPES.map((st) => (
                            <option key={st} value={st}>
                              {t(
                                `campaigns:registration.v2.secondaryTypes.${st}`,
                              )}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <Controller
                        name={`secondarySubmissions.${i}.quantity`}
                        control={control}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="number"
                            min={1}
                            className="w-16 px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                        )}
                      />
                      <span className="text-xs text-gray-500">
                        {itemType === 'video'
                          ? t('campaigns:registration.v2.minutes')
                          : t('campaigns:registration.v2.pieces')}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Controller
                      name={`secondarySubmissions.${i}.note`}
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        />
                      )}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => secondaryRemove(i)}>
                      <XMarkIcon className="w-4 h-4 text-gray-400" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button
          type="button"
          onClick={() => secondaryAppend({ type: '', quantity: 1, note: '' })}
          className="mt-2 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
        >
          <PlusIcon className="w-4 h-4" />{' '}
          {t('campaigns:registration.v2.addRow')}
        </button>
      </div>

      {/* 제출경로 */}
      <Controller
        name="submissionPath"
        control={control}
        render={({ field }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.submissionPath')}
            </label>
            <input
              {...field}
              type="text"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500"
            />
          </div>
        )}
      />

      {/* 2차 활용 제출 요청 가이드 */}
      <Controller
        name="secondarySubmissionGuide"
        control={control}
        render={({ field }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.secondaryGuide')}
            </label>
            <textarea
              {...field}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500"
            />
          </div>
        )}
      />
    </div>
  );
};

export default CampaignOtherV2;
